import { useState, useEffect } from 'react';
import axios from 'axios';
import { AdminLayout } from '../../components/AdminLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Users, MoreVertical, Play, Pause, Ban, Trash2, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, customer: null });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/admin/customers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCustomers(res.data);
    } catch (error) {
      console.error(error);
      toast.error('Erreur lors du chargement des clients');
    } finally {
      setLoading(false);
    }
  };

  const updateCustomerStatus = async (userId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/admin/customers/${userId}/status`, 
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Compte ${status === 'active' ? 'activé' : status === 'paused' ? 'mis en pause' : 'suspendu'}`);
      fetchCustomers();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
    setConfirmDialog({ open: false, action: null, customer: null });
  };

  const deleteCustomer = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/admin/customers/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Client supprimé');
      fetchCustomers();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
    setConfirmDialog({ open: false, action: null, customer: null });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'suspended':
        return <Badge className="bg-red-500 text-white"><Ban className="h-3 w-3 mr-1" /> Suspendu</Badge>;
      case 'paused':
        return <Badge className="bg-yellow-500 text-white"><Pause className="h-3 w-3 mr-1" /> En pause</Badge>;
      default:
        return <Badge className="bg-brand-gold text-white"><CheckCircle className="h-3 w-3 mr-1" /> Actif</Badge>;
    }
  };

  const handleAction = (action, customer) => {
    setConfirmDialog({ open: true, action, customer });
  };

  const confirmAction = () => {
    const { action, customer } = confirmDialog;
    if (action === 'delete') {
      deleteCustomer(customer.user_id);
    } else {
      updateCustomerStatus(customer.user_id, action);
    }
  };

  const getActionMessage = () => {
    const { action, customer } = confirmDialog;
    if (!customer) return '';
    switch (action) {
      case 'active':
        return `Voulez-vous réactiver le compte de ${customer.name} ?`;
      case 'paused':
        return `Voulez-vous mettre en pause le compte de ${customer.name} ? Le client ne pourra plus passer de commandes.`;
      case 'suspended':
        return `Voulez-vous suspendre le compte de ${customer.name} ? Le client ne pourra plus se connecter.`;
      case 'delete':
        return `Voulez-vous supprimer définitivement le compte de ${customer.name} ? Cette action est irréversible.`;
      default:
        return '';
    }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-customers">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl text-brand-black flex items-center gap-3">
              <Users className="h-8 w-8 text-brand-gold" />
              Gestion des Clients
            </h1>
            <p className="text-brand-dark/60 mt-1">{customers.length} clients inscrits</p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-brand-gold/10 border border-brand-gold/20 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-brand-gold" />
              <div>
                <p className="text-2xl font-bold text-brand-black">
                  {customers.filter(c => !c.account_status || c.account_status === 'active').length}
                </p>
                <p className="text-sm text-brand-dark/60">Comptes actifs</p>
              </div>
            </div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <Pause className="h-8 w-8 text-yellow-500" />
              <div>
                <p className="text-2xl font-bold text-brand-black">
                  {customers.filter(c => c.account_status === 'paused').length}
                </p>
                <p className="text-sm text-brand-dark/60">Comptes en pause</p>
              </div>
            </div>
          </div>
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <Ban className="h-8 w-8 text-red-500" />
              <div>
                <p className="text-2xl font-bold text-brand-black">
                  {customers.filter(c => c.account_status === 'suspended').length}
                </p>
                <p className="text-sm text-brand-dark/60">Comptes suspendus</p>
              </div>
            </div>
          </div>
        </div>

        {/* Customers Table */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-2 border-brand-gold border-t-transparent rounded-full animate-spin" />
          </div>
        ) : customers.length === 0 ? (
          <div className="text-center py-12 bg-brand-white rounded-xl border border-brand-gold/20">
            <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <p className="text-brand-dark/60">Aucun client inscrit</p>
          </div>
        ) : (
          <div className="bg-brand-white border border-brand-gold/20 rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-brand-gold/20 bg-brand-cream">
                    <TableHead className="text-brand-dark/80 font-semibold">Client</TableHead>
                    <TableHead className="text-brand-dark/80 font-semibold">Email</TableHead>
                    <TableHead className="text-brand-dark/80 font-semibold">Statut</TableHead>
                    <TableHead className="text-brand-dark/80 font-semibold">Inscription</TableHead>
                    <TableHead className="text-brand-dark/80 font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customers.map(customer => (
                    <TableRow key={customer.user_id} className="border-brand-gold/10 hover:bg-brand-cream">
                      <TableCell className="font-medium">
                        <div>
                          <p className="text-brand-black">{customer.name}</p>
                          <p className="text-xs text-brand-dark/50">{customer.user_id}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-brand-dark/80">{customer.email}</TableCell>
                      <TableCell>
                        {getStatusBadge(customer.account_status)}
                      </TableCell>
                      <TableCell className="text-brand-dark/60 text-sm">
                        {new Date(customer.created_at).toLocaleDateString('fr-FR', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            {customer.account_status !== 'active' && (
                              <DropdownMenuItem onClick={() => handleAction('active', customer)} className="text-brand-gold">
                                <Play className="h-4 w-4 mr-2" /> Réactiver
                              </DropdownMenuItem>
                            )}
                            {customer.account_status !== 'paused' && (
                              <DropdownMenuItem onClick={() => handleAction('paused', customer)} className="text-yellow-600">
                                <Pause className="h-4 w-4 mr-2" /> Mettre en pause
                              </DropdownMenuItem>
                            )}
                            {customer.account_status !== 'suspended' && (
                              <DropdownMenuItem onClick={() => handleAction('suspended', customer)} className="text-red-500">
                                <Ban className="h-4 w-4 mr-2" /> Suspendre
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => handleAction('delete', customer)} className="text-red-600">
                              <Trash2 className="h-4 w-4 mr-2" /> Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* Confirmation Dialog */}
        <Dialog open={confirmDialog.open} onOpenChange={(open) => setConfirmDialog({ ...confirmDialog, open })}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-brand-black">
                <AlertCircle className="h-5 w-5 text-brand-gold" />
                Confirmation
              </DialogTitle>
            </DialogHeader>
            <p className="text-brand-dark/80 py-4">{getActionMessage()}</p>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setConfirmDialog({ open: false, action: null, customer: null })}>
                Annuler
              </Button>
              <Button 
                onClick={confirmAction}
                className={confirmDialog.action === 'delete' ? 'bg-red-500 hover:bg-red-600' : 'bg-brand-gold hover:bg-brand-gold'}
              >
                Confirmer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
